/**
 * «Аномалии» раздела EO «Виджет» (01.11, ТЗ 1.17 EMB-019 ч. 5, §0.6 п. 70, SEC-010): 7 суток по Оренбургу. Из сводок прокладки по
 * экземплярам — 429 (лимит), отказы за чужой Origin и чужой ключ, заявки «под вопросом» по причинам, ответы V4 не по схеме; сети —
 * только хешем (16 hex, соль суток экземпляра), без IP. Своё у V4 по сайтам — ловушка, 429, заявки track:false по причинам.
 * Всплески — по порогам widgetAnomaly.ts (одно место); у ряда — какая тревога V4 это уже ловит. Без ПДн.
 */
import { BASE, useLoad } from "./widgetApi";
import { ANOMALY, anomalySeries, netRows } from "./widgetAnomaly";
import { NO_DATA, reasonText } from "./widgetFmt";
import type { Anomalies, Overview } from "./widgetTypes";
import css from "./widget.module.css";

const dd = (day: string) => `${day.slice(8, 10)}.${day.slice(5, 7)}`;

export function WidgetAnomalies() {
  const an = useLoad<Anomalies>(`${BASE}/v2/anomalies`);
  const ov = useLoad<Overview>(`${BASE}/v2/overview`);
  const d = an.data;
  const name = (id: number) => ov.data?.sites.find((s) => s.id === id)?.domain ?? `сайт №${id}`;
  const rows = d ? anomalySeries(d, name) : [], nets = d ? netRows(d) : [];
  const spikes = rows.filter((r) => r.spikes.some(Boolean)).length + nets.filter((n) => n.spike).length;
  return (
    <div className={css.card}>
      <div className={css.head}>
        <span className={css.title}>Аномалии · 7 суток</span>
        <button type="button" className={css.ghost} disabled={an.busy} onClick={an.reload}>Обновить</button>
      </div>
      {an.err ? <div className={css.err}>{an.err}</div> : null}
      {!d && !an.err ? <div className={css.muted}>Загружаю…</div> : null}
      {d ? (
        <>
          <div className={css.line}>
            <span className={`${css.badge} ${spikes ? css.fail : css.ok}`}>{spikes ? `всплесков: ${spikes}` : "всплесков нет"}</span>
            <span className={css.muted}>{d.windows === null ? `сводок прокладки: ${NO_DATA}` : `сводок прокладки за 7 суток: ${d.windows.reduce((a, w) => a + w.n, 0)}`}</span>
          </div>
          {!rows.length ? <div className={css.muted}>Событий за 7 суток нет{d.windows?.length ? "" : ` (сводок нет — ${NO_DATA})`}</div> : (
            <div className={css.tableWrap}>
              <table className={`${css.table} ${css.wide}`}>
                <thead><tr><th>что</th><th>где</th>{d.days.map((x) => <th key={x} className={css.num}>{dd(x)}</th>)}<th className={css.num}>всего</th><th>тревога V4</th></tr></thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.key}>
                      <td>{ANOMALY[r.rule].label}{r.detail ? `: ${r.rule === "schema" ? r.detail : reasonText(r.detail)}` : ""}</td>
                      <td>{r.scope}</td>
                      {r.values.map((n, i) => <td key={d.days[i]} className={`${css.num} ${r.spikes[i] ? css.spike : ""}`} title={r.spikes[i] ? "всплеск" : undefined}>{n || ""}{r.spikes[i] ? " ▲" : ""}</td>)}
                      <td className={css.num}>{r.total}</td>
                      <td className={css.muted}>{ANOMALY[r.rule].alert ?? "нет — только здесь"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className={css.sub}>Сети (хеш /24 или /48, соль суток экземпляра — сравнимы только в пределах суток и экземпляра)</div>
          {!nets.length ? <div className={css.muted}>{d.windows?.length ? "событий по сетям нет" : NO_DATA}</div> : (
            <div className={css.tableWrap}>
              <table className={`${css.table} ${css.wide}`}>
                <thead><tr><th>сутки</th><th>экземпляр</th><th>сеть</th><th className={css.num}>429</th><th className={css.num}>Origin</th><th className={css.num}>ключ</th><th className={css.num}>под вопросом</th><th className={css.num}>всего</th></tr></thead>
                <tbody>
                  {nets.map((n) => (
                    <tr key={`${n.day}-${n.instance}-${n.net}`} className={n.spike ? css.spike : undefined}>
                      <td>{dd(n.day)}</td><td>{n.instance}</td><td className={css.mono}>{n.net === "_other" ? "прочие сети" : n.net}</td>
                      <td className={css.num}>{n.limited}</td><td className={css.num}>{n.origin}</td><td className={css.num}>{n.key}</td><td className={css.num}>{n.suspect}</td>
                      <td className={css.num}>{n.total}{n.spike ? " ▲" : ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className={css.sub}>Нет данных</div>
          {Object.entries(d.missing).map(([k, v]) => <div key={k} className={css.row}>{v}</div>)}
          {d.v4 === null ? <div className={css.row}>Своих счётчиков V4 (ловушка, 429, track:false) — {NO_DATA}: заявок v2 ещё не было</div> : null}
          <div className={css.sub}>Пороги всплеска (сутки)</div>
          {Object.values(ANOMALY).map((r) => <div key={r.label} className={css.meta}>{r.label}: от {r.min}{r.factor > 1 ? ` и в ${r.factor} раза выше обычного` : ""}</div>)}
        </>
      ) : null}
    </div>
  );
}
